// packages/agent-kit/src/useAgentConfiguration.ts
import useSWR from 'swr';
import { useCallback, useMemo } from 'react';
import {
  agentService,
  promptService,
  toolService,
  billingService,
  isApiError,
} from '@dopomogai/api-client';
import type {
  PromptResponse,
  ToolResponse,
  PromptCreatePayload,
  UUID,
  LLMUsageSummaryResponse,
  AgentUsageDetail,
} from '@dopomogai/types';
import { AgentKitError } from '../types';

// --- HOOK FOR AGENT PROMPTS ---

interface AgentPromptsData {
  prompts: PromptResponse[];
  activePrompt: PromptResponse | null;
}

const agentPromptsFetcher = async ([, agentId]: [string, UUID]): Promise<AgentPromptsData> => {
  const [promptsResponse, activePromptResponse] = await Promise.all([
    agentService.listPromptsForAgent(agentId),
    agentService.getActivePromptForAgent(agentId),
  ]);

  if (isApiError(promptsResponse)) {
    throw new AgentKitError('Failed to fetch agent prompts.', promptsResponse);
  }
  // A 404 for active prompt is not a critical error, it just means none is active.
  if (isApiError(activePromptResponse) && activePromptResponse.status !== 404) {
    throw new AgentKitError('Failed to fetch active agent prompt.', activePromptResponse);
  }

  return {
    prompts: promptsResponse,
    activePrompt: isApiError(activePromptResponse) ? null : activePromptResponse,
  };
};

/**
 * Hook to fetch and manage an agent's prompts.
 * Provides data on all linked prompts, the active one, and actions to change them.
 */
export const useAgentPrompts = (agentId: UUID | null) => {
  const cacheKey = agentId ? [`agents/${agentId}/prompts`, agentId] : null;
  const { data, error, isLoading, mutate } = useSWR<AgentPromptsData, AgentKitError>(
    cacheKey,
    agentPromptsFetcher
  );

  
  const createAndActivatePrompt = useCallback(async (payload: PromptCreatePayload) => {
    if (!agentId) throw new AgentKitError("Agent ID is required to create and activate a prompt.");

    const newPrompt = await promptService.createPrompt(payload);
    if (isApiError(newPrompt)) {
      throw new AgentKitError("Failed to create the new prompt.", newPrompt);
    }

    const activatedPrompt = await agentService.setActivePromptForAgent(agentId, newPrompt.id);
    if (isApiError(activatedPrompt)) {
      throw new AgentKitError("Failed to activate the new prompt.", activatedPrompt);
    }
    
    await mutate(); // Re-fetch data to update the UI.
    return activatedPrompt;
  }, [agentId, mutate]);

  const setActivePrompt = useCallback(async (promptId: UUID) => {
    if (!agentId) throw new AgentKitError("Agent ID is required to set an active prompt.");

    const result = await agentService.setActivePromptForAgent(agentId, promptId);
    if (isApiError(result)) {
      throw new AgentKitError(`Failed to set prompt ${promptId} as active.`, result);
    }
    await mutate(); // Re-fetch data to update the UI.
    return result;
  }, [agentId, mutate]);

  const updateActivePromptContent = useCallback(async (newContent: string) => {
    if (!agentId) throw new AgentKitError("Agent ID is required.");
    if (!newContent || !newContent.trim()) throw new AgentKitError("Prompt content cannot be empty.");
  
    const saved = await promptService.savePromptVersionAndActivate(agentId, { content: newContent });
    if (isApiError(saved)) {
      throw new AgentKitError(`Failed to save/activate new prompt version.`, saved);
    }
    await mutate();
    return saved;
  }, [agentId, mutate]);

  return {
    prompts: data?.prompts,
    activePrompt: data?.activePrompt,
    isLoading,
    error,
    createAndActivatePrompt,
    setActivePrompt,
    updateActivePromptContent,
    mutate,
  };
};

// --- HOOK FOR AGENT TOOLS ---

interface AgentToolsData {
  agentToolSlugs: string[];
  availableTools: ToolResponse[];
}

const agentToolsFetcher = async ([, agentId]: [string, UUID]): Promise<AgentToolsData> => {
  const [agentToolsResponse, availableToolsResponse] = await Promise.all([
    agentService.getAgentTools(agentId),
    toolService.getAllTools(),
  ]);

  if (isApiError(agentToolsResponse)) throw new AgentKitError('Failed to fetch agent tools.', agentToolsResponse);
  if (isApiError(availableToolsResponse)) throw new AgentKitError('Failed to fetch available tools.', availableToolsResponse);

  return { agentToolSlugs: agentToolsResponse, availableTools: availableToolsResponse };
};

/**
 * Hook to fetch and manage an agent's tools.
 * Provides the agent's current tools, all available tools, and an action to update them.
 */
export const useAgentTools = (agentId: UUID | null) => {
  const cacheKey = agentId ? [`agents/${agentId}/tools`, agentId] : null;
  const { data, error, isLoading, mutate } = useSWR<AgentToolsData, AgentKitError>(cacheKey, agentToolsFetcher);

  const updateTools = useCallback(async (toolSlugs: string[]) => {
    if (!agentId) throw new AgentKitError("Agent ID is required to update tools.");

    const result = await agentService.updateAgentTools(agentId, toolSlugs);
    if (isApiError(result)) throw new AgentKitError("Failed to update agent tools.", result);
    
    // Optimistically update the local cache for a snappier UI response, then revalidate.
    mutate({ ...data!, agentToolSlugs: result }, false);
    return result;
  }, [agentId, mutate, data]);
  
  const agentTools = useMemo(() => {
    if (!data) return [];
    const agentSlugsSet = new Set(data.agentToolSlugs);
    return data.availableTools.filter(tool => agentSlugsSet.has(tool.slug));
  }, [data]);

  return {
    agentTools,
    availableTools: data?.availableTools,
    isLoading,
    error,
    updateTools,
    mutate,
  };
};



// ===================================================================
// ==           UPDATED HOOK FOR AGENT USAGE (This is the new code) ==
// ===================================================================

const USAGE_SUMMARY_CACHE_KEY = 'billing/usage-summary';

const usageFetcher = async (key: string): Promise<LLMUsageSummaryResponse> => {
    // The period can be made dynamic if needed in the future.
    // For now, it matches the "This Month" label in the UI.
    const response = await billingService.getUsageSummary("current_month");
    if (isApiError(response)) {
        throw new AgentKitError("Failed to fetch usage summary.", response);
    }
    return response;
}

/**
 * Hook to retrieve usage statistics for a specific agent.
 * Fetches the global usage summary for the current month and filters it
 * for the specified agent, benefiting from a shared SWR cache.
 *
 * @param agentId The UUID of the agent to get usage for. If null, the hook will not fetch data.
 * @returns An object containing the agent's specific usage, the full summary, and loading/error states.
 */
export const useAgentUsage = (agentId: UUID | null) => {
    const { data, error, isLoading } = useSWR<LLMUsageSummaryResponse, AgentKitError>(
        // Use a static key because the endpoint fetches data for all agents.
        // The hook is responsible for filtering. This allows multiple components
        // using this hook to share a single API call.
        // The fetch is only triggered if an agentId is provided.
        agentId ? USAGE_SUMMARY_CACHE_KEY : null,
        usageFetcher,
        {
            // Optional: Revalidate on focus can be disabled if stats don't need to be live.
            revalidateOnFocus: false,
        }
    );

    const agentUsage: AgentUsageDetail | undefined = useMemo(() => {
        // This is the core logic. The backend now provides the `usage_by_agent` array.
        // We just need to find the specific agent's data in that array.
        if (!data || !agentId || !data.usage_by_agent) {
            return undefined;
        }
        return data.usage_by_agent.find(usage => usage.agent_id === agentId);
    }, [data, agentId]);

    return {
        /** Usage details for the specific agent, or undefined if not found or while loading. */
        usage: agentUsage,
        /** The entire usage summary response from the API, containing data for all agents. */
        fullSummary: data,
        /** True if the usage data is currently being fetched. */
        isLoading,
        /** An error object if the fetch failed. */
        error,
    };
};